<template>
  <div class="commodity-list">
    <DataTable
      v-model:filters="filters"
      :value="commodities"
      :paginator="true"
      :rows="10"
      :rowsPerPageOptions="[5, 10, 25, 50]"
      :loading="loading"
      responsiveLayout="stack"
      breakpoint="960px"
      :rowHover="true"
      class="p-datatable-sm"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} commodities"
      v-model:selection="selectedCommodity"
      selectionMode="single"
      dataKey="id"
      filterDisplay="row"
      :globalFilterFields="['name', 'category', 'is_active']"
      @value-change="onDataChange"
    >
      <template #header>
        <div class="flex justify-end">
          <IconField>
            <InputIcon>
              <i class="pi pi-search" />
            </InputIcon>
            <InputText v-model="filters['global'].value" placeholder="Search all fields..." />
          </IconField>
        </div>
      </template>

      <template #empty>
        <div class="flex flex-column align-items-center py-5">
          <i class="pi pi-inbox text-6xl text-200 mb-4"></i>
          <p class="text-500">No commodities found.</p>
        </div>
      </template>

      <template #loading>
        <div class="flex flex-column align-items-center py-5">
          <i class="pi pi-spin pi-spinner text-6xl text-primary mb-4"></i>
          <p>Loading commodities...</p>
        </div>
      </template>

      <Column field="name" header="Name" sortable style="min-width: 12rem"></Column>

      <Column field="category" header="Category" sortable style="min-width: 12rem"></Column>

      <Column
        field="current_price_per_lb"
        header="Price per lb ($)"
        sortable
        style="min-width: 12rem"
      >
        <template #body="slotProps">
          {{ formatCurrency(slotProps.data.current_price_per_lb) }}
        </template>
      </Column>

      <Column field="is_active" header="Status" sortable style="min-width: 10rem">
        <template #body="slotProps">
          <Tag
            :value="slotProps.data.is_active ? 'Active' : 'Inactive'"
            :severity="slotProps.data.is_active ? 'success' : 'danger'"
          />
        </template>
      </Column>

      <Column field="updated_at" header="Last Updated" sortable style="min-width: 12rem">
        <template #body="slotProps">
          {{ formatDate(slotProps.data.updated_at) }}
        </template>
      </Column>

      <Column header="Actions" :exportable="false" style="min-width: 10rem">
        <template #body="slotProps">
          <div class="flex gap-2 justify-content-center">
            <Button
              :icon="slotProps.data.is_favorite ? 'pi pi-star-fill' : 'pi pi-star'"
              :class="[
                'p-button-rounded p-button-text',
                slotProps.data.is_favorite ? 'p-button-warning' : '',
              ]"
              @click="$emit('toggle-favorite', slotProps.data)"
              :aria-label="
                slotProps.data.is_favorite ? 'Remove from favorites' : 'Add to favorites'
              "
            />
            <Button
              icon="pi pi-pencil"
              class="p-button-rounded p-button-text"
              @click="$emit('edit-commodity', slotProps.data)"
              aria-label="Edit Commodity"
            />
            <Button
              icon="pi pi-trash"
              class="p-button-rounded p-button-text p-button-danger"
              @click="$emit('delete-commodity', slotProps.data)"
              aria-label="Delete Commodity"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { FilterMatchMode } from '@primevue/core/api'
import type { CommodityType } from '@/types/CommodityType'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Tag from 'primevue/tag'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'

import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'

const props = defineProps({
  commodities: {
    type: Array as () => CommodityType[],
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'edit-commodity',
  'delete-commodity',
  'select-commodity',
  'toggle-favorite',
])

const selectedCommodity = ref(null)

// Filter configuration
const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
})

const onDataChange = (items: CommodityType[]) => {
  const categories = [...new Set(items.map((item) => item.category))]
  console.log('Unique commodity categories:', categories)
  console.log('Total items:', items.length)
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value)
}

function formatDate(timestamp: any): string {
  if (!timestamp) return 'N/A'
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date)
}
</script>
